import React, { useEffect, useState } from "react";
import algoliasearch from "algoliasearch";
import {
  InstantSearch,
  Hits,
  Configure
} from "react-instantsearch";
import { createStore, applyMiddleware } from "redux";
import { Provider, connect } from "react-redux";
import thunk from "redux-thunk";
import { Button } from "react-bootstrap";

import rootReducer from "../reducers";
import { fetchAllCartItems, addCartItemRequest, cartItemIds } from "../actions/index";

const searchClient = algoliasearch(window.algoliaConfig.appId, window.algoliaConfig.searchApiKey);
const indexName = window.algoliaConfig.indexName;

// Create Redux store
const store = createStore(rootReducer, applyMiddleware(thunk));

// Initialize cart items
store.dispatch(fetchAllCartItems());

// Hit component (copied from Main.js with same functionality)
const Hit = connect(state => ({
  cart_items_ids: cartItemIds(state.cart_items)
}))(({ hit, dispatch, cart_items_ids, ownedProductIds }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [isHighResLoaded, setIsHighResLoaded] = useState(false);
  const productId = hit.objectID || hit.id;
  const isInCart = cart_items_ids.includes(productId);
  const isOwned = ownedProductIds.includes(productId);

  // Load high-res image after component mounts
  useEffect(() => {
    if (hit.full_image) {
      const img = new Image();
      img.src = hit.full_image;
      img.onload = () => {
        setIsHighResLoaded(true);
      };
    }
  }, [hit.full_image]);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAdding(true);
    try {
      await dispatch(addCartItemRequest(productId));
    } catch (error) {
      console.error("Failed to add item to cart:", error);
    } finally {
      setIsAdding(false);
    }
  };



    const handleCardClick = (e) => {
    // Only navigate if not clicking on interactive elements
    if (e.target.closest('.pack-cart-button') || e.target.closest('.preset-pack-image')) {
      return;
    }
    e.preventDefault();
    window.location.href = `/preset_packs/${hit.slug}`;
  };

  const handleImageClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Always navigate to details page since we removed audio functionality
    window.location.href = `/preset_packs/${hit.slug}`;
  };

  return (
    <div className="preset-pack-card" data-card="true" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
      <div
        className="preset-pack-image clickable-nav"
        onClick={handleImageClick}
        title="Click to view details"
      >
        {hit.mid_image && (
          <div className="image-container">
            <img
              src={hit.mid_image}
              alt={hit.name}
              className={`pack-artwork ${isHighResLoaded ? 'high-res-loaded' : ''}`}
              style={{ filter: isHighResLoaded ? 'none' : 'blur(10px)' }}
            />
            {hit.full_image && (
              <img
                src={hit.full_image}
                alt={hit.name}
                className={`pack-artwork high-res ${isHighResLoaded ? 'visible' : ''}`}
                style={{ opacity: isHighResLoaded ? 1 : 0 }}
              />
            )}
          </div>
        )}
        {/* Special badges for exclusive, featured, new - overlaid on image */}
        {(hit.exclusive || hit.featured || hit.is_new) && (
          <div className="pack-badges-overlay">
            {hit.exclusive && <span className="badge badge-exclusive">Exclusive</span>}
            {hit.featured && <span className="badge badge-featured">Featured</span>}
            {hit.is_new && <span className="badge badge-new">New</span>}
          </div>
        )}
      </div>
      <div className="preset-pack-content">
        <div className="pack-header">
          <h4 className="pack-title">{hit.name}</h4>
          {hit.manufacturer_name && (
            <p className="pack-manufacturer">by {hit.manufacturer_name}</p>
          )}
        </div>

        {/* Genre list */}
        {hit.genres && hit.genres.length > 0 && (
          <div className="genre-list">
            {hit.genres.slice(0, 4).join(', ')}
            {hit.genres.length > 4 && (
              <span className="genre-more"> +{hit.genres.length - 4} more</span>
            )}
          </div>
        )}

        {/* Serum sections */}
        {(hit.serum_1_presets || hit.serum_2_presets) && (
          <div className="preset-info">
            {hit.serum_1_presets && (
              <span className="preset-count">{hit.serum_1_presets} Serum 1</span>
            )}
            {hit.serum_2_presets && (
              <span className="preset-count">{hit.serum_2_presets} Serum 2</span>
            )}
          </div>
        )}

        {/* Cart button */}
        <div className="pack-actions">
          {isAdding ? (
            <Button className="pack-cart-button" variant="secondary" disabled>
              <i className="fa fa-spinner fa-spin"></i>
            </Button>
          ) : (isOwned || isInCart) ? (
            <Button className="pack-cart-button" variant="success" disabled>
              <i className="fa fa-check"></i>
            </Button>
          ) : (
            <Button
              onClick={handleAddToCart}
              className="pack-cart-button"
              variant="primary"
            >
              ${hit.price || '0.00'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

// Connected component inside the Provider
const ConnectedFeaturedSerum2PacksInner = connect(state => ({
  cart_items_ids: cartItemIds(state.cart_items)
}))(({ ownedProductIds, cart_items_ids }) => {
  useEffect(() => {
    // Inject CSS to ensure 4-column layout
    const style = document.createElement('style');
    style.type = 'text/css';
    style.innerHTML = `
      .featured-serum2-packs .ais-Hits-list {
        display: flex !important;
        flex-direction: row !important;
        justify-content: center !important;
        gap: 1.5rem !important;
        flex-wrap: nowrap !important;
        width: 100% !important;
        margin: 0 auto !important;
      }
      .featured-serum2-packs .ais-Hits-item {
        flex: 0 0 280px !important;
        max-width: 280px !important;
      }
      .featured-serum2-packs .btn {
        margin: 0.5rem !important;
        padding: 0.75rem 2rem !important;
        border-radius: 8px !important;
        font-weight: 500 !important;
        background: #4CAF50 !important;
        border: none !important;
        color: white !important;
        transition: all 0.3s ease !important;
        text-decoration: none !important;
      }
      .featured-serum2-packs .btn:hover {
        transform: translateY(-3px) !important;
        box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2) !important;
        color: white !important;
        background: #45a049 !important;
      }
      .featured-serum2-packs .shadow-pulse {
        animation: shadow-pulse 2s infinite !important;
      }
      @keyframes shadow-pulse {
        0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7); }
        70% { box-shadow: 0 0 0 10px rgba(76, 175, 80, 0); }
        100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
      }
      @media (max-width: 1200px) {
        .featured-serum2-packs .ais-Hits-item {
          flex: 0 0 240px !important;
          max-width: 240px !important;
        }
      }
      @media (max-width: 768px) {
        .featured-serum2-packs .ais-Hits-list {
          flex-direction: column !important;
          align-items: center !important;
        }
        .featured-serum2-packs .ais-Hits-item {
          flex: 0 0 auto !important;
          max-width: 400px !important;
          width: 100% !important;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      // Cleanup
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={indexName}
      future={{
        preserveSharedStateOnUnmount: true
      }}
    >
      <Configure
        hitsPerPage={50}
        filters="active:true"
      />
      <div className="featured-serum2-packs">
        <div className="container">
          <div
            style={{
              display: 'flex',
              flexWrap: 'nowrap',
              justifyContent: 'center',
              gap: '1.5rem',
              maxWidth: '1300px',
              margin: '0 auto',
              width: '100%'
            }}
          >
            <div style={{ display: 'flex', width: '100%', justifyContent: 'center', gap: '1.5rem' }}>
              <Hits
                hitComponent={(props) => <Hit {...props} ownedProductIds={ownedProductIds} />}
                transformItems={(items) => {
                  // Filter for featured items with Serum 2 presets
                  const featuredSerum2Items = items.filter(item =>
                    item.featured &&
                    item.serum_2_presets &&
                    item.serum_2_presets > 0
                  );

                  // If no featured Serum 2 items, try just Serum 2 items
                  const serum2Items = items.filter(item =>
                    item.serum_2_presets &&
                    item.serum_2_presets > 0
                  );

                  // Choose which items to show with multiple fallback levels
                  let itemsToShow = featuredSerum2Items;
                  if (itemsToShow.length === 0) {
                    itemsToShow = serum2Items;
                  }
                  if (itemsToShow.length === 0) {
                    // Final fallback - show any 4 random items
                    itemsToShow = items;
                  }

                  // Filter out items the user already owns or has in cart
                  const availableItems = itemsToShow.filter(item => {
                    const productId = String(item.objectID || item.id);
                    const isOwned = ownedProductIds.includes(productId);
                    const isInCart = cart_items_ids && cart_items_ids.includes(productId);
                    return !isOwned && !isInCart;
                  });

                  // Shuffle and take 4
                  const shuffled = [...availableItems].sort(() => 0.5 - Math.random());
                  return shuffled.slice(0, 4);
                }}
              />
            </div>
          </div>
          <div className="text-center mt-4">
            <a href="/preset_packs" className="btn btn-lg shadow-pulse">
              View All Preset Packs
            </a>
          </div>
        </div>
      </div>
    </InstantSearch>
  );
});

// Main component for featured Serum 2 packs
const FeaturedSerum2Packs = (props) => {
  const [ownedProductIds, setOwnedProductIds] = useState([]);

  useEffect(() => {
    const initialIds = props.ownedProductIds || [];
    setOwnedProductIds(initialIds.map(String)); // Ensure IDs are strings
  }, [props.ownedProductIds]);

  return (
    <Provider store={store}>
      <ConnectedFeaturedSerum2PacksInner ownedProductIds={ownedProductIds} />
    </Provider>
  );
};

export default FeaturedSerum2Packs;